import { ChangeEvent, useState } from 'react';
import toast from 'react-hot-toast';
import { namespaces, useSharedTranslation } from 'us-react-components/dist/hooks/useSharedTranslation';
import { useNavigate } from 'react-router-dom';

import { RatingDesktop } from 'us-react-components/dist/Rating/RatingDesktop';
import { RatingMobile } from 'us-react-components/dist/Rating/RatingMobile';
import { SpecificFeedback } from 'us-react-components/dist/Rating/SpecificFeedback';
import { FeedbackService } from 'us-web-services';
import { Button, ButtonType, Link } from '../components';
import { Storage, ToastTiming } from '../constants';
import { useDependencies } from '../dependencies';
import { Routes } from '../Routes';

export const Feedback = (): JSX.Element => {
  document.title = 'UrbanSitter - Feedback';
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { stores } = useDependencies();

  const [comment, setComment] = useState<string>();
  const [rating, setRating] = useState<number>();
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(false);

  const hasRating = (): boolean => rating !== undefined && rating >= 0;

  const redirectToSource = (): void => {
    const { dashboard } = Routes.caregiver;
    let route = localStorage.getItem(Storage.NPS_FEEDBACK_SOURCE) ?? dashboard;
    if (route === Routes.feedback) {
      route = dashboard;
    }
    navigate(route);
  };

  const handleClick = async (): Promise<void> => {
    try {
      setSubmitDisabled(true);
      const feedback = {
        userId: stores.user.user.id,
        rating: rating!,
        comment,
      };

      await FeedbackService.post(feedback);

      toast.success(t('notifications.feedback.success'), { icon: '🎉' });

      // TODO: remove this setTimeout when we remove angular
      setTimeout(() => {
        redirectToSource();
      }, ToastTiming.DISPLAY_TIME_SUCCESS);
    } catch {
      setSubmitDisabled(false);
    }
  };

  const handleRatingChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setRating(parseInt(e.target.value, 10));
  };

  const handleRatingChangeMobile = (e: ChangeEvent<HTMLSelectElement>): void => {
    setRating(parseInt(e.target.value, 10));
  };

  const handleCommentChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    setComment(e.target.value);
  };

  return (
    <article className='container py-10'>
      <h1 className='text-2xl font-semibold'>{t('components.feedback.header')}</h1>
      <h2 className='text-xl font-semibold'>{t('components.feedback.subheader')}</h2>
      <RatingDesktop onChange={handleRatingChange} />
      <RatingMobile onChange={handleRatingChangeMobile} rating={rating} />
      <div className='max-w-[43rem] lg:max-w-[54rem]'>
        <div className='mt-2 hidden md:flex'>
          <span className='flex-auto'>{t('components.feedback.notLikely')}</span>
          <span className='flex-auto text-right'>{t('components.feedback.veryLikely')}</span>
        </div>
        {hasRating() && (
          <>
            <SpecificFeedback Link={Link} onChange={handleCommentChange} />
            <Button buttonType={ButtonType.primary} onClick={handleClick} disabled={!hasRating() || submitDisabled}>
              {t('components.feedback.sendFeedback')}
            </Button>
          </>
        )}
      </div>
    </article>
  );
};
